// EegPatientDashboard.js
// Patient view: personal recovery progress, what the brain is doing, top exercises.
// Language is plain English — no clinical jargon visible to the patient.

import React, { useEffect, useState, useCallback } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";

// Auth / API helpers
import { getToken, getUser } from "redux/services";
import {
  EEG_PATIENT_API,
  EEG_PROGRESS_API,
  EEG_PROGRESS_ERD_API,
  EEG_PROGRESS_EX_API,
  EEG_SESSION_LATEST_API,
  EEG_ALERTS_API,
  EEG_PROTOCOL_API,
} from "redux/requests/apis";

/* ── helpers ───────────────────────────────────────────────────────────── */

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
});

async function apiFetch(url) {
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

/* ── Panel header ───────────────────────────────────────────────────────── */
const PanelHeader = ({ icon, title, color = "dark" }) => (
  <MDBox display="flex">
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="4rem"
      height="4rem"
      variant="gradient"
      bgColor={color}
      color="white"
      shadow="md"
      borderRadius="xl"
      ml={3}
      mt={-2}
    >
      <Icon fontSize="medium" color="inherit">{icon}</Icon>
    </MDBox>
    <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
      {title}
    </MDTypography>
  </MDBox>
);

/* ── Recovery gauge (0–100 visual) ─────────────────────────────────────── */
function RecoveryGauge({ value, label }) {
  // Asymmetry index ranges from –1 (extreme) to 0 (balanced). Map to 0–100%.
  const pct = value != null ? Math.max(0, Math.min(100, Math.round((1 - Math.abs(value)) * 100))) : null;
  return (
    <MDBox textAlign="center">
      <MDTypography variant="h2" fontWeight="bold" color={pct > 70 ? "success" : pct > 40 ? "warning" : "error"}>
        {pct != null ? `${pct}%` : "—"}
      </MDTypography>
      <MDTypography variant="caption" color="text">{label}</MDTypography>
      <LinearProgress
        variant="determinate"
        value={pct ?? 0}
        sx={{
          mt: 1, height: 10, borderRadius: 5,
          backgroundColor: "#e0e0e0",
          "& .MuiLinearProgress-bar": {
            backgroundColor: pct > 70 ? "#4caf50" : pct > 40 ? "#ff9800" : "#f44336",
          },
        }}
      />
    </MDBox>
  );
}

/* ── Exercise card ──────────────────────────────────────────────────────── */
function ExerciseCard({ rank, label, effectiveness }) {
  const pct = Math.round(Math.max(0, Math.min(100, Math.abs(effectiveness ?? 0) * 100)));
  const color = rank === 1 ? "success" : rank === 2 ? "info" : "warning";
  return (
    <MDBox display="flex" alignItems="center" gap={2} mb={1.5}>
      <MDBox
        display="flex" justifyContent="center" alignItems="center"
        width="2rem" height="2rem" borderRadius="50%"
        sx={{ background: rank === 1 ? "#4caf50" : rank === 2 ? "#2196f3" : "#ff9800" }}
      >
        <MDTypography variant="button" color="white" fontWeight="bold">
          {rank}
        </MDTypography>
      </MDBox>
      <MDBox flex={1}>
        <MDTypography variant="button" fontWeight="medium">{label || `Exercise ${rank}`}</MDTypography>
        <LinearProgress
          variant="determinate" value={pct}
          sx={{
            height: 6, borderRadius: 3, mt: 0.5,
            backgroundColor: "#e0e0e0",
            "& .MuiLinearProgress-bar": {
              backgroundColor: rank === 1 ? "#4caf50" : rank === 2 ? "#2196f3" : "#ff9800",
            },
          }}
        />
      </MDBox>
      <MDTypography variant="caption" color="text">{pct}%</MDTypography>
    </MDBox>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════════════════════════════════ */
export default function EegPatientDashboard() {
  const userId = getUser();

  const [patient,  setPatient]  = useState(null);
  const [progress, setProgress] = useState(null);
  const [erdData,  setErdData]  = useState([]);
  const [exercises,setExercises]= useState([]);
  const [latest,   setLatest]   = useState(null);
  const [alerts,   setAlerts]   = useState(null);
  const [protocol, setProtocol] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const load = useCallback(async () => {
    if (!userId) { setError("Please log in."); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const [pat, prog, erd, ex, lat, alrt, prot] = await Promise.allSettled([
        apiFetch(EEG_PATIENT_API(userId)),
        apiFetch(EEG_PROGRESS_API(userId)),
        apiFetch(EEG_PROGRESS_ERD_API(userId)),
        apiFetch(EEG_PROGRESS_EX_API(userId)),
        apiFetch(EEG_SESSION_LATEST_API(userId)),
        apiFetch(EEG_ALERTS_API(userId)),
        apiFetch(EEG_PROTOCOL_API(userId)),
      ]);
      if (pat.status  === "fulfilled") setPatient(pat.value);
      if (prog.status === "fulfilled") setProgress(prog.value);
      if (erd.status  === "fulfilled") setErdData(erd.value);
      if (ex.status   === "fulfilled") setExercises(ex.value);
      if (lat.status  === "fulfilled") setLatest(lat.value);
      if (alrt.status === "fulfilled") setAlerts(alrt.value);
      if (prot.status === "fulfilled") setProtocol(prot.value);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  /* ── charts ─────────────────────────────────────────────────────────── */
  const erdChart = {
    labels: erdData.map((d) => `Session ${d.sessionNumber}`),
    datasets: [
      {
        label: "Brain Engagement Score",
        color: "success",
        data: erdData.map((d) => {
          // Convert ERD (negative %) into a positive 0–100 engagement score
          return Math.max(0, Math.min(100, Math.round(Math.abs(d.erdAlpha ?? 0))));
        }),
      },
    ],
  };

  /* ── best exercises across all sessions ─────────────────────────────── */
  const topExercises = (() => {
    if (!exercises.length) return [];
    // Aggregate effectiveness per exercise label across sessions
    const map = {};
    exercises.forEach(({ exerciseResults }) => {
      (exerciseResults || []).forEach(({ label, effectiveness }) => {
        if (!map[label]) map[label] = { label, total: 0, count: 0 };
        map[label].total += Math.abs(effectiveness ?? 0);
        map[label].count += 1;
      });
    });
    return Object.values(map)
      .map((e) => ({ label: e.label, effectiveness: e.total / e.count }))
      .sort((a, b) => b.effectiveness - a.effectiveness)
      .slice(0, 5);
  })();

  if (loading) return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox display="flex" justifyContent="center" mt={8}><CircularProgress /></MDBox>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>

        {error && <Alert severity="info" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ── Greeting ──────────────────────────────────────────────── */}
        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="bold">
            Your Recovery Progress
          </MDTypography>
          {patient && (
            <MDTypography variant="body2" color="text">
              Session {progress?.totalSessions ?? "—"} of 14 · Day {patient.daysSinceStroke} post-stroke
            </MDTypography>
          )}
        </MDBox>

        {/* ── Row 1: Recovery gauge + next session ─────────────────── */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%", p: 3 }}>
              <PanelHeader icon="self_improvement" title="Brain Balance Score" color="info" />
              <MDBox px={2} pb={2} mt={1}>
                <RecoveryGauge
                  value={progress?.latestAsymmetry}
                  label="How balanced your brain activity is (higher = better)"
                />
                <MDBox mt={2} textAlign="center">
                  <MDTypography variant="caption" color="text">
                    Day 1: {progress?.day1AsymmetryIndex?.toFixed(3) ?? "—"} &nbsp;→&nbsp;
                    Now: {progress?.latestAsymmetry?.toFixed(3) ?? "—"}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%", p: 3 }}>
              <PanelHeader icon="electric_bolt" title="Brain Activation" color="success" />
              <MDBox px={2} pb={2} mt={1} textAlign="center">
                <MDTypography variant="h2" fontWeight="bold"
                  color={progress?.erdNowPresent ? "success" : "error"}>
                  {progress?.erdNowPresent ? "Active" : "Weak"}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  Motor cortex signal during hand movements
                </MDTypography>
                <MDBox mt={2}>
                  <MDTypography variant="body2">
                    Brain engagement improved by{" "}
                    <strong>{Math.abs(progress?.erdChange ?? 0).toFixed(1)}%</strong>{" "}
                    since first session.
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%", p: 3 }}>
              <PanelHeader icon="schedule" title="Next Session" color="dark" />
              <MDBox px={2} pb={2} mt={1}>
                {protocol ? (
                  <>
                    <MDBox
                      p={1.5} borderRadius="lg" mb={1}
                      sx={{ background: "linear-gradient(195deg, #42424a, #191919)" }}
                    >
                      <MDTypography variant="button" color="white">
                        Focus: <strong>{protocol.primaryElectrode}</strong> electrode
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="caption" color="text">
                      {protocol.rationale}
                    </MDTypography>
                    <MDBox mt={1}>
                      <Chip
                        size="small"
                        icon={<Icon>{protocol.clinicianApproved ? "check_circle" : "pending"}</Icon>}
                        label={protocol.clinicianApproved ? "Clinician approved" : "Awaiting clinician approval"}
                        color={protocol.clinicianApproved ? "success" : "default"}
                      />
                    </MDBox>
                  </>
                ) : (
                  <MDTypography variant="button" color="text" fontStyle="italic">
                    Your therapist has not yet set up a protocol.
                  </MDTypography>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* ── Row 2: Brain engagement chart ────────────────────────── */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={7}>
            {erdData.length > 0 ? (
              <DefaultLineChart
                icon={{ color: "success", component: "trending_up" }}
                title="Your Brain Engagement Over Sessions"
                description="How strongly your motor cortex activates during exercises — higher is better"
                height="14rem"
                chart={erdChart}
              />
            ) : (
              <Card sx={{ height: "14rem" }}>
                <PanelHeader icon="trending_up" title="Brain Engagement Over Sessions" color="success" />
                <MDBox px={3} pt={1}>
                  <MDTypography variant="button" color="text" fontStyle="italic">
                    Complete your first EEG session to see your progress chart.
                  </MDTypography>
                </MDBox>
              </Card>
            )}
          </Grid>

          {/* ── Latest session quick stats ──────────────────────────── */}
          <Grid item xs={12} md={5}>
            <Card sx={{ height: "100%" }}>
              <PanelHeader icon="summarize" title="Latest Session" color="info" />
              {latest ? (
                <MDBox px={3} pb={3}>
                  <MDBox display="flex" justifyContent="space-between" py={0.5}>
                    <MDTypography variant="button" color="text">Session number</MDTypography>
                    <MDTypography variant="button" fontWeight="bold">{latest.sessionNumber}</MDTypography>
                  </MDBox>
                  <MDBox display="flex" justifyContent="space-between" py={0.5}>
                    <MDTypography variant="button" color="text">Brain signal detected</MDTypography>
                    <MDTypography variant="button" color={latest.erdPresent ? "success" : "error"} fontWeight="bold">
                      {latest.erdPresent ? "Yes" : "Not yet"}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" justifyContent="space-between" py={0.5}>
                    <MDTypography variant="button" color="text">Best exercise</MDTypography>
                    <MDTypography variant="button" fontWeight="bold">
                      {latest.rankedExercises?.[0] || "—"}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" justifyContent="space-between" py={0.5}>
                    <MDTypography variant="button" color="text">Sensory pathway</MDTypography>
                    <MDTypography variant="button" color={latest.sensoryPathwayIntact ? "success" : "warning"} fontWeight="bold">
                      {latest.sensoryPathwayIntact ? "Intact" : "Reduced"}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              ) : (
                <MDBox px={3} pb={3}>
                  <MDTypography variant="button" color="text" fontStyle="italic">
                    No session data yet.
                  </MDTypography>
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>

        {/* ── Row 3: Top exercises ──────────────────────────────────── */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <PanelHeader icon="fitness_center" title="Your Most Effective Exercises" color="warning" />
              <MDBox px={3} pb={3} mt={1}>
                {topExercises.length > 0 ? (
                  topExercises.map((ex, i) => (
                    <ExerciseCard key={ex.label} rank={i + 1} label={ex.label} effectiveness={ex.effectiveness} />
                  ))
                ) : (
                  <MDTypography variant="button" color="text" fontStyle="italic">
                    Exercise rankings will appear after your first session.
                  </MDTypography>
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* ── Alerts visible to patient (plain language) ───────────── */}
          <Grid item xs={12} md={6}>
            <Card>
              <PanelHeader icon="notifications" title="Notes from Your Therapist" color="error" />
              <MDBox px={3} pb={3} mt={1}>
                {alerts?.count === 0 && (
                  <Alert severity="success">Everything looks good from your last session.</Alert>
                )}
                {(alerts?.alerts || []).map((a, i) => (
                  <Alert key={i} severity="info" sx={{ mb: 1 }}>
                    {translateAlert(a)}
                  </Alert>
                ))}
                {!alerts && (
                  <MDTypography variant="button" color="text" fontStyle="italic">
                    No notes yet.
                  </MDTypography>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

      </MDBox>
    </DashboardLayout>
  );
}

/* ── Convert clinical alert text to patient-friendly language ───────────── */
function translateAlert(alert) {
  if (alert.includes("depression"))   return "Your therapist would like to check in about how you are feeling emotionally.";
  if (alert.includes("ERD"))          return "The brain signal during hand movement exercises needs more practice — keep going!";
  if (alert.includes("somatosensory")) return "Your sense of touch and position in your arm may need extra attention this week.";
  return alert;
}
